import { AppState } from '@om-fe/core/core/types';
import {
    listenToMessage,
    ReceivedMessageType,
    sendMessage,
    SendMessageType,
} from '@om-fe/core/utils/parent-communication';
import { BettingHistoryType, CashOutServerType, SendCashOutResponse, CashoutAbleTabType } from '@om-fe/api';
import { AutoCashoutStatus, SSECashoutValueCodes, BetStatus, SessionInfoModel, AuthenticationMethodType } from '@om-fe/models';
import { OddsFormat, BemMethod, createBem, getStorage, setStorage, PlatformTypes } from '@om-fe/utils';
import { BettingHistoryDetailsParams, WidgetsRouteChangeDetails, WidgetsRouteName  } from '@om-fe/widgets/dist/common/routing/routing.constants';
import { BettingHistoryItemLayout } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item.layout';
import { BettingHistoryItemV2ClassModifiers } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item/v2/betting-history-item-v2.utils';
import { BettingHistoryList } from '@om-fe/widgets/dist/widgets/betting-history/list/betting-history-list';
import { CashoutItemStateModel, CashoutValueState } from '@om-fe/widgets/dist/widgets/cashout/cashout.utils';
import { Checkbox } from '@om-fe/widgets/dist/components/checkbox/checkbox';
import { CheckboxType } from '@om-fe/widgets/dist/components/checkbox/checkbox-constants';
import { RangePickerValue } from '@om-fe/widgets/dist/components/range-picker/rangepicker';
import { SettledBetFilterType } from '@om-fe/widgets/dist/widgets/betting-history/list/betting-history-list.utils';
import { withSelectionBetslipData } from '@om-fe/widgets/dist/widgets/betslip/with-betslip-selection-data/with-betslip-selection-data';
import { getOpenBetsV2Counter } from '@om-fe/widgets/dist/widgets/betslip/utils/betslip.utils.ts';
import { useLang } from '@om-fe/widgets/dist/common/i18n/i18n.hooks';
import React, { ComponentType, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { BetslipOpenBetsTabs, BetslipTabs } from './betslip-tabs';
import { BetslipTaxes } from './betslip-taxes';
import getConfig, { BetslipConfig, DateFormatConfigKey, getConfigDateFormatType, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { BettingHistoryCashoutAbleHeader, BettingHistoryOpenBetsV2WithLiveStatusHeader } from '@om-fe/core/components/betting-history/betting-history-status-header';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { SettledBetsFilters } from '../betslip-wrapper/settled-bets-filters/settled-bets-filters';
import dayjs from 'dayjs';
import { IsAuthenticatedStatus, useApiConfigHost, useAuthenticatedStatus, useAuthenticationMethodType, useAuthId, useOperatorId, useSessionInfo } from '@om-fe/store';
import { BetslipHistoryVirtualTabs, BetslipVirtualBetsTabs } from './betslip-history-virtual-tabs';

export const aocBem: BemMethod = createBem('BetslipExpandAll');
export const BETTING_DETAIL_EXPAND_STORAGE_KEY = 'OMFE:bettingDetailExpand';
export const filtersBem: BemMethod = createBem('BetlispFiltersWrapper');

export interface BetslipOpenBetsV2Props {
    readonly className?: string;
    readonly openModal?: () => void;
    readonly closeModal?: () => void;
    readonly parentContainerRef: MutableRefObject<React.ReactNode>;
    readonly onTabChanged?: (tab: BetslipOpenBetsTabs) => void;
}

export interface BetslipOpenBetsStoreProps {
    readonly oddsFormat: OddsFormat;
    readonly selectionList: string[];
    readonly deviceType?: PlatformTypes;
}

const config: OMFE2ConfigModel = getConfig();

const BetslipOpenBetsV2Component = (
    {
        oddsFormat,
        selectionList,
        openModal,
        closeModal,
        parentContainerRef,
        onTabChanged,
        deviceType,
    }: BetslipOpenBetsV2Props & BetslipOpenBetsStoreProps) => {
    const lang: string = useLang();
    const [selectedTab, setSelectedTab] = useState<BetslipOpenBetsTabs>(BetslipOpenBetsTabs.BetSlip);
    const [openBetsPage, setOpenBetsPage] = useState<number>(1);
    const [showDetailsForBet, setShowBetId] = useState<string | undefined>();
    const [openBetsCount, setOpenBetsCount] = useState<number | undefined>(undefined);
    const [openLiveBetsCount, setOpenLiveBetsCount] = useState<number>(0);
   
    const sessionInfo: SessionInfoModel | undefined = useSessionInfo();
    const userId: string | number = sessionInfo?.userIdStr || sessionInfo?.userID || 0;
    const userSessionId: string | undefined = useAuthId();
    const operatorId: number = useOperatorId();
    const host: string | undefined = useApiConfigHost();
    const authType: AuthenticationMethodType | undefined = useAuthenticationMethodType();
    const [selectedSecondaryTab, setSecondaryTab] = useState<BettingHistoryType>(config?.betslip?.defaultTabMyBets ?? BettingHistoryType.liveBets);
    const authStatus: IsAuthenticatedStatus = useAuthenticatedStatus();
    const [filterType, setFilterType] = useState<SettledBetFilterType>(SettledBetFilterType.PlacementDate);
    const [filterStatus, setFilterStatus] = useState<BetStatus | undefined>(undefined);
    const [searchStatus, setSearchStatus] = useState<BetStatus | undefined>(undefined);
    const [showSettledBetsFiltersModal, setShowSettledBetsFiltersModal] = useState<boolean>(false);
    const showWinTaxPercentage: boolean = config.bettingHistory?.showWinTaxPercentage ?? false;
    const showMatchTime: boolean = config.bettingHistory?.showMatchTime ?? false;
    const isFetchingCounterRef = useRef<boolean>(false);
    const retryFetchCounterTimeoutRef = useRef<number | undefined>(undefined);
    const betslip: BetslipConfig = config.betslip;
    const betSelectionsExpandedByDefault: boolean = betslip?.betSelectionsExpandedByDefault ?? false;
    const enableBetExpansion: boolean = betslip?.enableBetExpansion ?? false;
    const lastFullyExpandedRef = useRef<boolean>(betSelectionsExpandedByDefault);
    const enabledSettledBetV2: boolean = betslip?.enabledSettledBetV2 ?? false;
    const calendarDateLimit: number = betslip?.numberOfDaysLimitSettleBetFilters || (betslip?.disabledLimitSettleBetFilters ? 1080 : 90);
    const virtualTabConfig: boolean = betslip?.enabledVirtualHistoryTabs ?? false;
    const [virtualTabs, setVirtualTabs] = useState<BetslipVirtualBetsTabs>(BetslipVirtualBetsTabs.AllBets);
    const currentDate: Date = dayjs().startOf('date').toDate();
    const defaultStartDate: Date = dayjs(currentDate).subtract(calendarDateLimit - 1, 'day').toDate();
    const maxStartDate: Date = dayjs().endOf('date').toDate();
    const [filterStartDate, setFilterStartDate] = useState<string | undefined>(betslip?.disabledSettleBetFiltersModal ? dayjs(defaultStartDate).startOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
    const [filterEndDate, setFilterEndDate] = useState<string | undefined>(betslip?.disabledSettleBetFiltersModal ? dayjs(maxStartDate).endOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
    const [currentFilterPage, setCurrentFilterPage] = useState<number>(1);
    const [selectedCashoutAbleTab, setSelectedCashoutAbleTab] = useState<CashoutAbleTabType>(CashoutAbleTabType.all);
    const longLiveToken: string | undefined = sessionInfo?.longLiveTokenForBetsAPI;
    const betsDetailsSortable: boolean = betslip?.betsDetailsSortable ?? false;
    const hasSimulateBetButton: boolean = !!config?.simulate?.showSimulateBetButton;
    const enabledVirtualTab: boolean = virtualTabConfig || hasSimulateBetButton;

    const reFetchHistory = useCallback(async () => {
        if (!host || !operatorId || !userId || !userSessionId || !authType || !lang || isFetchingCounterRef.current) {
            return;
        }

        const fetchCounter = async () => {
            isFetchingCounterRef.current = true;

            if (retryFetchCounterTimeoutRef.current) {
                clearTimeout(retryFetchCounterTimeoutRef.current);
                retryFetchCounterTimeoutRef.current = undefined;
            }

            try {
                const response = await getOpenBetsV2Counter({
                    host,
                    operatorId: `${operatorId}`,
                    userId: `${userId}`,
                    userSessionId,
                    authType,
                    lang,
                    longLiveToken,
                });
                if (response && response.noOfBets) {
                    setOpenBetsCount(response.noOfBets);
                }
            } finally {
                isFetchingCounterRef.current = false;
            }
        };

        try {
            await fetchCounter();
        } catch (error) {
            if (retryFetchCounterTimeoutRef.current) {
                clearTimeout(retryFetchCounterTimeoutRef.current);
            }

            retryFetchCounterTimeoutRef.current = window.setTimeout(() => {
                retryFetchCounterTimeoutRef.current = undefined;
                void reFetchHistory();
            }, 5000);
        }
    }, [host, operatorId, userId, userSessionId, authType, lang, longLiveToken]);

    const [filterDateRange, setDateRange] = useState<RangePickerValue>(() => ({
        start: dayjs(defaultStartDate).toDate().getTime(),
        end: dayjs(maxStartDate).toDate().getTime(),
    }));

    const handlePageChange = useCallback((newPage: number) => {
        if (selectedSecondaryTab === BettingHistoryType.settledBets) {
            setCurrentFilterPage(newPage);
        } else {
            setOpenBetsPage(newPage);
        }
    }, [selectedSecondaryTab, setCurrentFilterPage, setOpenBetsPage]);

    const handleOpenSettledBetsFilters = useCallback(() => {
        setShowSettledBetsFiltersModal(true);
    }, []);

    const handleCloseSettledBetsFilters = useCallback(() => {
        setCurrentFilterPage(1);
        setShowSettledBetsFiltersModal(false);
        setFilterStartDate(filterDateRange.start ? dayjs(filterDateRange.start).startOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
        setFilterEndDate(filterDateRange.end ? dayjs(filterDateRange.end).endOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
        setSearchStatus(filterStatus);
    }, [filterDateRange, filterStatus]);

    const handleFilterChange = useCallback((tab: SettledBetFilterType) => {
        if (tab === SettledBetFilterType.PlacementDate) {
            setFilterType(tab);
        }
    }, []);

    const resetExpandOnTabSwitch = useCallback(() => {
        if (enableBetExpansion) {
            if (betSelectionsExpandedByDefault) {
                setAllBetExpanded(true);
                setStorage<boolean>(BETTING_DETAIL_EXPAND_STORAGE_KEY, true);
            } else {
                setAllBetExpanded(lastFullyExpandedRef.current);
            }
            setExpandAllKey(k => k + 1);
        }
    }, []);

    const handleFilterStatusChange = useCallback((status: BetStatus | undefined) => {
        setFilterStatus(status);
        if (betslip?.disabledSettleBetFiltersModal) {
            setSearchStatus(status);
        }
        resetExpandOnTabSwitch();
    }, [betslip?.disabledSettleBetFiltersModal, resetExpandOnTabSwitch]);

    const handleDateRangeClear = useCallback(() => {
        setFilterStartDate(dayjs(defaultStartDate).startOf('date').format('YYYY-MM-DDTHH:mm:ss'));
        setFilterEndDate(dayjs(maxStartDate).endOf('date').format('YYYY-MM-DDTHH:mm:ss'));
        setDateRange({ start: dayjs(defaultStartDate).toDate().getTime(), end: dayjs(maxStartDate).toDate().getTime() });
    }, [defaultStartDate, maxStartDate]);

    const handleDateRangeChange = useCallback((range: RangePickerValue) => {
        setDateRange({ start: range.start, end: range.end ? range.end : range.start });
    }, []);

    const handleSelectTab = useCallback((tab: BetslipOpenBetsTabs) => {
        onTabChanged?.(tab);
        setSelectedTab(tab);
    }, [onTabChanged]);

    const updateListWithDelay = useCallback(() => {
        // hide backend bug where count does not update right when the cashout is executed
        setTimeout(() => {
            sendMessage({ type: 'OMFE:cashoutSuccessfully' as SendMessageType });
        }, 2000);
    }, []);

    const handleCashoutDataChanged = useCallback((cashoutData: CashoutItemStateModel | CashoutValueState | undefined) => {
        const v2Cashout = cashoutData as CashoutValueState | undefined;
        if (v2Cashout?.cashout?.details?.code === SSECashoutValueCodes.BetHasBeenAlreadySettled) {
            updateListWithDelay();
        }
    }, [updateListWithDelay]);

    const handleCashout = useCallback((response: SendCashOutResponse) => {
        if (response?.success) {
            updateListWithDelay();
        }
    }, [updateListWithDelay]);

    const handleAutoCashoutStatusChanged = useCallback((status: AutoCashoutStatus) => {
        if (status === AutoCashoutStatus.BetSettled) {
            updateListWithDelay();
        }
    }, [updateListWithDelay]);

    const handlePlaceBetSuccess = useCallback(() => {
        void reFetchHistory();
    }, [reFetchHistory]);

    const handleRouteChange = useCallback(
        (details: WidgetsRouteChangeDetails<WidgetsRouteName.Tournament | WidgetsRouteName.Match | WidgetsRouteName.BettingHistoryDetails>) => {
            if (details.name === WidgetsRouteName.BettingHistoryDetails) {
                setShowBetId((details.params as BettingHistoryDetailsParams).betId);
            }
        },
        []
    );

    const handleSecondaryTab = useCallback((status: BettingHistoryType) => {
        setOpenBetsPage(1);
        setCurrentFilterPage(1);
        setSecondaryTab(status);
        setSelectedCashoutAbleTab(CashoutAbleTabType.all);
        resetExpandOnTabSwitch();
    }, [resetExpandOnTabSwitch]);

    const handleCashoutTab = useCallback((status: CashoutAbleTabType) => {
        setOpenBetsPage(1);
        setCurrentFilterPage(1);
        setSelectedCashoutAbleTab(status);
        resetExpandOnTabSwitch();
    }, [resetExpandOnTabSwitch]);

    useEffect(() => {
        setSelectedTab(BetslipOpenBetsTabs.BetSlip);
    }, [selectionList]);

    useEffect(() => {
        return listenToMessage('OP:showOpenBets' as ReceivedMessageType, () => {
            setSelectedTab(BetslipOpenBetsTabs.OpenBets);
        });
    }, []);

    useEffect(() => {
        return listenToMessage('OP:showBetslip' as ReceivedMessageType, () => {
            setSelectedTab(BetslipOpenBetsTabs.BetSlip);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (retryFetchCounterTimeoutRef.current) {
                clearTimeout(retryFetchCounterTimeoutRef.current);
                retryFetchCounterTimeoutRef.current = undefined;
            }
        };
    }, []);

    useEffect(() => {
        void reFetchHistory();
    }, [host, operatorId, userId, userSessionId, authType, reFetchHistory]);

    useEffect(() => {
        if (selectedTab === BetslipOpenBetsTabs.OpenBets) {
            setSelectedCashoutAbleTab(CashoutAbleTabType.all);
        }
    }, [selectedTab]);

    const { gettext } = useI18NContext();
    const hideBettingDetail: boolean = getStorage<boolean>(BETTING_DETAIL_EXPAND_STORAGE_KEY) || betSelectionsExpandedByDefault ? betSelectionsExpandedByDefault : false;

    const initialExpanded = enableBetExpansion
        ? betSelectionsExpandedByDefault
        : hideBettingDetail;
    const [allBetExpanded, setAllBetExpanded] = useState<boolean>(initialExpanded);
    // Separate from `allBetExpanded` (the toggle) to prevent infinite loop on initialize. 
    // This will report the actual expand status from the list.
    const [isFullyExpanded, setIsFullyExpanded] = useState<boolean>(initialExpanded);
    // Since we separate the `allBetExpanded` (the toggle state) from the fact from the list. 
    // We need a way to sync both of them when swiching tab around or when on partial mode like so.
    const [expandAllKey, setExpandAllKey] = useState<number>(0);
    const expandAllDisplayState: boolean = enableBetExpansion ? isFullyExpanded : allBetExpanded;

    const onExpandStatusChange = useCallback((status: boolean)=>{
        setIsFullyExpanded(status);
        lastFullyExpandedRef.current = status;
        setStorage<boolean>(BETTING_DETAIL_EXPAND_STORAGE_KEY, status);
    },[])

    const handleToggle = useCallback(() => {
        if(enableBetExpansion){
            const next = !expandAllDisplayState;
            setAllBetExpanded(next);
            setStorage<boolean>(BETTING_DETAIL_EXPAND_STORAGE_KEY, next);
            if (next) {
                setExpandAllKey(k => k + 1);
            }
        } else{
            setAllBetExpanded(current => !current);
            setShowBetId('');
            setStorage<boolean>(BETTING_DETAIL_EXPAND_STORAGE_KEY, !allBetExpanded);
        }
        
    }, [expandAllDisplayState, allBetExpanded]);

    const openBetsUpdateCounter = useCallback((counter: number, liveCounter: number) => {
        setOpenBetsCount(counter);
        setOpenLiveBetsCount(liveCounter);
    }, []);
    
    const taxPercentage: number | undefined = betslip?.taxPercentage;
    const showMarketnameOnly: boolean | undefined = betslip?.showMarketnameOnly;
    const enabledRebet: boolean = betslip?.enabledRebet ?? false;

    useEffect(() => {
        return () => {
            if (selectedSecondaryTab === BettingHistoryType.settledBets && enabledSettledBetV2) {
                setDateRange({
                    start: dayjs(defaultStartDate).toDate().getTime(),
                    end: dayjs(maxStartDate).toDate().getTime(),
                });
                setFilterStatus(undefined);
                setSearchStatus(undefined);
                setFilterStartDate(betslip?.disabledSettleBetFiltersModal ? dayjs(defaultStartDate).startOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
                setFilterEndDate(betslip?.disabledSettleBetFiltersModal ? dayjs(maxStartDate).endOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
            }
        }
    }, [selectedSecondaryTab]);

    const handleVirtualTabChange = useCallback((tab: BetslipVirtualBetsTabs) => {
        setVirtualTabs(tab);
        if (tab === BetslipVirtualBetsTabs.SimulateBetsV2) {
            handleSecondaryTab(BettingHistoryType.settledBets);
        }
        resetExpandOnTabSwitch();
    }, [handleSecondaryTab, resetExpandOnTabSwitch]);

    const {
        desktop: showOnDesktop = false,
        tablet: showOnTablet = false,
        mobile: showOnMobile = false,
    } = betslip?.shareBetButtonOnOpenBets ?? {};

    const isDesktop: boolean = deviceType === 'desktop';
    const isTablet: boolean = deviceType === 'tablet';
    const isMobile: boolean = deviceType === 'mobile';

    const showShareBet: boolean = (showOnDesktop && isDesktop) || (showOnTablet && isTablet) || (showOnMobile && isMobile);

    return (
        <>
            <BetslipTabs
                selectedTab={selectedTab}
                openBetsCount={openBetsCount}
                onTabClick={handleSelectTab}
                closeModal={closeModal}
            />
            {(selectedTab === BetslipOpenBetsTabs.BetSlip) ? (
                <BetslipTaxes
                    parentContainerRef={parentContainerRef}
                    onPlaceBetSuccess={handlePlaceBetSuccess}
                    openModal={openModal}
                    closeModal={closeModal}
                />
            ) : (
                <>
                    {enabledVirtualTab && (
                        <BetslipHistoryVirtualTabs
                            selectedTab={virtualTabs}
                            onTabClick={handleVirtualTabChange}
                            hasVirtualBets={virtualTabConfig}
                            hasSimulateBetsV2={hasSimulateBetButton}
                        />
                    )}
                    {virtualTabs !== BetslipVirtualBetsTabs.SimulateBetsV2 && (
                        <BettingHistoryOpenBetsV2WithLiveStatusHeader
                            onStatusChanged={handleSecondaryTab}
                            status={selectedSecondaryTab}
                            preventRedirect={true}
                            openBetsCount={openBetsCount}
                            liveBetsCount={openLiveBetsCount}
                        />
                    )}
                    <div className={filtersBem()}>
                        {
                            authStatus === IsAuthenticatedStatus.IsLoggedIn &&
                            betslip?.settledBetsFilters &&
                            selectedSecondaryTab === BettingHistoryType.settledBets &&
                            virtualTabs !== BetslipVirtualBetsTabs.SimulateBetsV2 && (
                                <SettledBetsFilters
                                    tabActive={filterType}
                                    statusActive={filterStatus}
                                    onTabChanged={handleFilterChange}
                                    onStatusChanged={handleFilterStatusChange}
                                    onClose={handleCloseSettledBetsFilters}
                                    onOpen={handleOpenSettledBetsFilters}
                                    opened={showSettledBetsFiltersModal}
                                    minStartDate={defaultStartDate}
                                    filterDateRange={filterDateRange}
                                    maxStartDate={maxStartDate}
                                    handleDateRangeChange={handleDateRangeChange}
                                    useModal={betslip?.disabledSettleBetFiltersModal ? false : true}
                                    allBetExpanded={expandAllDisplayState}
                                    handleToggle={handleToggle}
                                    handleDateRangeClear={handleDateRangeClear}
                                    deviceType={deviceType}
                                />
                            )
                        }
                        {(!betslip?.disabledSettleBetFiltersModal || !!(betslip?.disabledSettleBetFiltersModal && betslip?.settledBetsFilters && selectedSecondaryTab !== BettingHistoryType.settledBets)) && 
                            <div className={aocBem()}>
                                <Checkbox
                                    inputType={CheckboxType.CHECKBOX_SWITCH}
                                    name="checkboxSwitch"
                                    label={expandAllDisplayState ? gettext('Collapse all') : gettext('Expand all')}
                                    checked={expandAllDisplayState}
                                    onChange={handleToggle}
                                />
                            </div>
                        }
                    </div>
                    {selectedSecondaryTab !== BettingHistoryType.settledBets && virtualTabs !== BetslipVirtualBetsTabs.SimulateBetsV2 &&
                        <BettingHistoryCashoutAbleHeader
                            onStatusChanged={handleCashoutTab}
                            status={selectedCashoutAbleTab}
                        />
                    }
                    <BettingHistoryList
                        classModifier={BettingHistoryItemV2ClassModifiers.Big}
                        className="OM-BettingHistory--OpenBetsView"
                        allBetExpanded={allBetExpanded}
                        itemLayout={BettingHistoryItemLayout.V3}
                        betStatus={selectedSecondaryTab}
                        nrOfRecords={20}
                        onPageChange={handlePageChange}
                        page={openBetsPage}
                        bettingHistoryDetailsRoute={false}
                        onAfterCashout={handleCashout}
                        oddsFormat={oddsFormat}
                        cashoutServer={CashOutServerType.v2}
                        onAutoCashoutStatusChanged={handleAutoCashoutStatusChanged}
                        onCashoutDataChanged={handleCashoutDataChanged}
                        hasShareBet={showShareBet}
                        pageRangeDisplayed={2}
                        marginPagesDisplayed={1}
                        onRouteChange={handleRouteChange}
                        betIdExpanded={showDetailsForBet}
                        showCashoutPending={config.cashoutPending}
                        filterPage={currentFilterPage}
                        filterType={selectedSecondaryTab === BettingHistoryType.settledBets ? filterType : undefined}
                        searchStatus={selectedSecondaryTab === BettingHistoryType.settledBets ? searchStatus: undefined}
                        startDate={selectedSecondaryTab === BettingHistoryType.settledBets ? filterStartDate : undefined}
                        endDate={selectedSecondaryTab === BettingHistoryType.settledBets ?  filterEndDate : undefined}
                        showDateSeparator={selectedSecondaryTab === BettingHistoryType.settledBets ? true : false}
                        taxPercentage={taxPercentage}
                        enabledDateOnMultiple={betslip?.enabledDateOnMultiple}
                        showMarketnameOnly={showMarketnameOnly}
                        pinIcon={!!(betslip?.systemBetV2?.pinIcon)}
                        matchDateFormat={config.dateFormat?.FormatDay}
                        enabledCashoutAble={true}
                        enabledSettledBetV2={enabledSettledBetV2}
                        showCashoutOnly={selectedCashoutAbleTab === CashoutAbleTabType.cashoutAble ? true : false}
                        openBetsUpdateCounter={openBetsUpdateCounter}
                        enabledRebet={enabledRebet}
                        dateFormat={getConfigDateFormatType(DateFormatConfigKey.FormatFullDate)}
                        showWinTaxPercentage={showWinTaxPercentage}
                        showMatchTime={showMatchTime}
                        matchTimeDateFormat={getConfigDateFormatType(DateFormatConfigKey.FormatDay)}
                        virtualTabs={enabledVirtualTab ? virtualTabs : undefined}
                        betsDetailsSortable={betsDetailsSortable}
                        onExpandStatusChange={enableBetExpansion ? onExpandStatusChange : undefined}
                        enableBetExpansion={enableBetExpansion}
                        expandAllKey={enableBetExpansion ? expandAllKey : undefined}
                    />
                </>
            )}
        </>
    );
};

export const BetslipOpenBetsV2: ComponentType<BetslipOpenBetsV2Props> = compose<BetslipOpenBetsV2Props>(
    withSelectionBetslipData(),
    connect((state: AppState) => {
        return {
            oddsFormat: state.settings.oddsFormat,
            deviceType: state.deviceInfo?.platform.type,
        };
    }),
)(BetslipOpenBetsV2Component);
